import { Loader } from '@deque/cauldron-react';
import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IssueFieldSource, JiraFieldMappers } from '@deque/ui-export-jira';
import { type FieldMappingField } from '~/app/types';
import { useGlobalToast } from '~/common/contexts/globalToast';
import { capitalize } from '~/common/utils/capitalize';
import type {
  IssueType,
  ProjectsAndIssueTypes
} from '~/common/utils/integrations-client/jira/getProjectsAndIssueTypes';
import { useFieldsContext } from '../../TemplateWizardForm/IssueTypeFieldsContext';
import { getValueToDisplay } from '../FieldMapping/Field/LabelFieldAssignment';
import { getAxeFieldsOptions } from '../FieldMapping/FieldMapping';
import StepReview from './StepReview/StepReview';

type ReviewProps = {
  isPriorityEnabled: boolean;
  setIsStepDataLoading: Dispatch<SetStateAction<boolean>>;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  getProjectById: (id: string) => ProjectsAndIssueTypes | undefined;
  getIssueTypeById: (id: string) => IssueType | undefined;
  setIsStepDataError: Dispatch<SetStateAction<boolean>>;
};

const mappers = Object.values(JiraFieldMappers);

const Review = ({
  isPriorityEnabled,
  setIsStepDataLoading,
  setCurrentStep,
  getProjectById,
  getIssueTypeById,
  setIsStepDataError
}: ReviewProps) => {
  const { t } = useTranslation();
  const { setContents } = useGlobalToast();
  const { getValues } = useFormContext();

  const { data: fields, loading, error, getPriorityById } = useFieldsContext();

  useEffect(() => setIsStepDataLoading(loading), [loading]);

  useEffect(() => {
    setIsStepDataError(!!error);

    if (error) {
      setContents(
        t('There was an error fetching fields for this issue type.'),
        'error'
      );
    }
  }, [error]);

  const formValue = getValues();

  const axeFieldsOptions = useMemo(() => getAxeFieldsOptions(t), [t]);

  const emptyFieldMappingInfo = {
    name: null,
    value: null
  };
  const fieldMappingFields = useMemo(() => {
    const fieldMapping = formValue.fieldMapping.map(
      ({ axeField, mappingField, sourceType }: FieldMappingField) => {
        if (!fields) {
          return emptyFieldMappingInfo;
        }

        const issueTypeField = fields.find(
          ({ fieldId }) => mappingField === fieldId
        );
        if (!issueTypeField) {
          return emptyFieldMappingInfo;
        }

        const mapperForThisField = mappers.find(mapper => {
          const field = fields.find(({ key }) => key === issueTypeField.key);

          return !!field && mapper.canMapThisField(field);
        });
        if (!mapperForThisField) {
          return emptyFieldMappingInfo;
        }

        let name = '';
        if (mapperForThisField.isEnum) {
          const selectedAllowedValue = issueTypeField.allowedValues?.find(
            allowedValue => allowedValue.id === axeField
          );

          name =
            selectedAllowedValue?.value || selectedAllowedValue?.name || '';
        } else if (sourceType === IssueFieldSource.Combined) {
          const separatedLabels = axeField.split(',');

          const formattedLabels = separatedLabels.map(label => {
            const [source, value, rest] = label.split(':');

            if (source === IssueFieldSource.FixedValue) {
              return `${t('Jira label: ')} ${getValueToDisplay[IssueFieldSource.FixedValue]?.(t, value)}`;
            } else if (source === IssueFieldSource.AxePartial) {
              return `${t('Axe tag: ')} ${getValueToDisplay[IssueFieldSource.AxePartial]?.(t, rest)}`;
            } else if (source === IssueFieldSource.Axe) {
              return `${t('Axe field: ')} ${getValueToDisplay[IssueFieldSource.Axe]?.(t, value)}`;
            }

            return '';
          });

          name = formattedLabels.filter(label => label !== '').join(', ');
        } else {
          name =
            axeFieldsOptions.find(({ value }) => axeField === value)?.label ||
            '';
        }
        return {
          name,
          value: fields?.find(({ fieldId }) => mappingField === fieldId)?.name
        };
      }
    );

    const summaryPrefix = formValue.summaryPrefix;

    return [
      ...fieldMapping,
      {
        name: t('Summary Prefix'),
        value: summaryPrefix
      }
    ];
  }, [formValue, fields, axeFieldsOptions]);

  const sections = [
    {
      title: t('Template Information'),
      fields: [
        {
          name: t('Template Name'),
          value: formValue.templateName
        },
        {
          name: t('Project Name'),
          value: getProjectById(formValue.project)?.name
        },
        {
          name: t('Issue Type'),
          value: getIssueTypeById(formValue.issueType)?.name
        },
        {
          name: t('Enable Axe Issue screenshots as attachments'),
          value: formValue.attachScreenshot ? 'Enabled' : 'Disabled'
        }
      ],
      onEdit: () => setCurrentStep(0)
    },
    {
      title: t('Field Mapping'),
      fields: fieldMappingFields,
      onEdit: () => setCurrentStep(1)
    }
  ];

  if (isPriorityEnabled) {
    sections.push({
      title: t('Impact Mapping'),
      fields: Object.entries(formValue.impactMapping).map(([key, value]) => ({
        name: t('{{ impact }} Impact', { impact: capitalize(key) }),
        value: value
          ? getPriorityById(value as string)?.name
          : t('Nothing selected')
      })),
      onEdit: () => setCurrentStep(2)
    });
  }

  if (loading) {
    return <Loader label={t('Loading template step...')} />;
  }

  return (
    <div>
      {sections.map(section => (
        <StepReview {...section} key={section.title} />
      ))}
    </div>
  );
};

export default Review;
